import React, { createContext, useContext, useState, useCallback } from 'react';

interface SupportChatVisibilityContextValue {
  isOpen: boolean;
  isMinimized: boolean;
  openChat: () => void;
  closeChat: () => void;
  minimizeChat: () => void;
  toggleChat: () => void;
}

const SupportChatVisibilityContext = createContext<SupportChatVisibilityContextValue | null>(null);

export function useSupportChatVisibility() {
  const context = useContext(SupportChatVisibilityContext);
  if (!context) {
    throw new Error('useSupportChatVisibility must be used within SupportChatVisibilityProvider');
  }
  return context;
}

export function useSupportChatVisibilityOptional() {
  return useContext(SupportChatVisibilityContext);
}

interface SupportChatVisibilityProviderProps {
  children: React.ReactNode;
}

export function SupportChatVisibilityProvider({ children }: SupportChatVisibilityProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const openChat = useCallback(() => {
    setIsOpen(true);
    setIsMinimized(false);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
  }, []);

  const minimizeChat = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const toggleChat = useCallback(() => {
    if (isOpen && !isMinimized) {
      setIsMinimized(true);
    } else {
      setIsOpen(true);
      setIsMinimized(false);
    }
  }, [isOpen, isMinimized]);

  const value: SupportChatVisibilityContextValue = {
    isOpen,
    isMinimized,
    openChat,
    closeChat,
    minimizeChat,
    toggleChat,
  };

  return (
    <SupportChatVisibilityContext.Provider value={value}>
      {children}
    </SupportChatVisibilityContext.Provider>
  );
}
